import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useApp } from "@/contexts/AppContext";
import { updateProfile } from "@/lib/api/account";
import { shippingAreasQuery, type ShippingArea } from "@/lib/api/admin";
import { profileQuery, myOrdersQuery } from "@/lib/api/queries";
import type { Profile } from "@/lib/api/types";
import { useFormatters, imgUrl, rowImg } from "@/lib/format";
import { orderStatusBadge } from "@/lib/order-status";
import { Icon } from "@/components/store/Icon";
import { Reveal } from "@/components/motion/Reveal";
import { StoreLayout } from "@/components/layout/StoreLayout";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";


const PROFILE_SECTION_KEY = "seen.profile-section";

const inputClass =
  "bg-surface-container-low border border-outline-variant/50 rounded-lg px-4 py-3 focus:ring-0 focus:border-primary transition-colors font-body-md text-primary outline-none";

const EMPTY_PROFILE: Profile = {
  fullName: "",
  phone: "",
  street: "",
  city: "",
  governorate: "",
};

const labelClass = "font-label-md text-label-md text-primary uppercase tracking-wider";

type Section = "shipping" | "orders" | "personal";

const NAV: { key: Section; icon: string; labelKey: string }[] = [
  { key: "shipping", icon: "local_shipping", labelKey: "profile.shippingProfile" },
  { key: "orders", icon: "receipt_long", labelKey: "profile.myOrders" },
  { key: "personal", icon: "person", labelKey: "profile.personalInfo" },
];

const NAV_KEYS: Section[] = ["shipping", "orders", "personal"];

function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { user, signOut } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isRtl = i18n.language?.startsWith("ar");
  const isCustomer = !!user && user.role !== "admin" && user.role !== "super_admin";

  const { data: savedData, isLoading: profileLoading } = useQuery({
    ...profileQuery(),
    enabled: isCustomer,
  });

  const { data: areas } = useQuery(shippingAreasQuery());

  const { data: orders = [] } = useQuery({
    ...myOrdersQuery(),
    enabled: isCustomer,
  });

  const saved: Profile = savedData ?? EMPTY_PROFILE;
  const { formatPrice } = useFormatters();
  const navigate = useNavigate();

  const requestedSection = searchParams.get("section") as Section | null;
  const storedSection =
    typeof window === "undefined" ? null : (window.sessionStorage.getItem(PROFILE_SECTION_KEY) as Section | null);
  const sectionCandidate = requestedSection ?? storedSection;
  const section: Section = NAV_KEYS.includes(sectionCandidate as Section)
    ? (sectionCandidate as Section)
    : "shipping";

  const [form, setForm] = useState<Profile>(saved);
  useEffect(() => setForm(saved), [saved]);

  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "super_admin")) {
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  const saveMutation = useMutation({
    mutationFn: () => updateProfile(form),
    onSuccess: () => toast.success(t("profile.saved")),
    onError: (error: Error) => toast.error(error.message),
  });

  const signOutMutation = useMutation({
    mutationFn: signOut,
    onSuccess: () => {
      toast.success(t("profile.signedOut"));
      navigate("/auth");
    },
  });

  const handleSectionChange = (sec: Section) => {
    window.sessionStorage.setItem(PROFILE_SECTION_KEY, sec);
    setSearchParams({ section: sec }, { replace: true });
    setMobileOpen(false);
  };

  if (isCustomer && profileLoading) {
    return (
      <StoreLayout>
        <main className="pt-10 pb-section-gap-desktop px-grid-margin max-w-7xl mx-auto flex items-center justify-center min-h-[50vh]">
          <div className="flex flex-col items-center gap-4 text-on-surface-variant">
            <Icon name="refresh" className="animate-spin text-4xl text-primary" />
            <p className="font-label-md text-label-md">{t("common.loading")}</p>
          </div>
        </main>
      </StoreLayout>
    );
  }

  const areaLabel = (area: ShippingArea) => area.name;
  const selectedArea = (areas ?? []).find((area) => area.name === form.governorate);
  const dirty = JSON.stringify(saved) !== JSON.stringify(form);
  const displayName = user ? `${user.firstName} ${user.lastName}`.trim() || user.email : saved.fullName;

  /** Sidebar nav content — shared between desktop static div and mobile Sheet */
  const SidebarNav = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-headline-md text-headline-md text-primary mb-1">{t("profile.title")}</h1>
        <p className="font-label-sm text-label-sm text-on-surface-variant/70 uppercase tracking-widest">
          {t("profile.welcome", { name: displayName })}
        </p>
      </div>

      {/* Nav items */}
      <nav className="flex-1 flex flex-col gap-2">
        {NAV.map((item, index) => (
          <div key={item.key}>
            {index === 2 && <div className="h-px bg-outline-variant/30 my-3" />}
            <button
              type="button"
              onClick={() => handleSectionChange(item.key)}
              className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition-all group ${
                section === item.key
                  ? "bg-secondary-container/20 text-secondary font-bold border-s-4 border-secondary"
                  : "text-on-surface-variant hover:bg-surface-container"
              }`}
            >
              <Icon name={item.icon} className="text-[20px] group-hover:text-primary" />
              <span className="font-label-md text-label-md">{t(item.labelKey)}</span>
            </button>
          </div>
        ))}
      </nav>

      {/* Sign out */}
      <button
        type="button"
        onClick={() => signOutMutation.mutate()}
        className="mt-6 w-full flex items-center gap-3 px-4 py-4 rounded-lg text-destructive hover:bg-destructive/10 transition-all"
      >
        <Icon name="logout" className="text-[20px]" />
        <span className="font-label-md text-label-md">{t("profile.signOut")}</span>
      </button>
    </div>
  );

  return (
    <StoreLayout>
      <main className="pt-10 pb-section-gap-desktop px-grid-margin max-w-7xl mx-auto">

        {/* ── Mobile top bar (hidden md+) ─────────────────────── */}
        <div className="md:hidden flex items-center justify-between mb-6 bg-surface-container-lowest border border-outline-variant/20 rounded-xl px-4 py-3 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0">
              {displayName.charAt(0).toUpperCase()}
            </div>
            <span className="font-bold text-sm truncate max-w-[160px]">{displayName}</span>
          </div>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-surface-container text-on-surface-variant transition-colors"
              >
                <Icon name={mobileOpen ? "close" : "menu"} className="text-xl" />
              </button>
            </SheetTrigger>
            <SheetContent
              side={isRtl ? "right" : "left"}
              className="w-72 p-6 bg-surface-container-lowest flex flex-col"
            >
              <SidebarNav />
            </SheetContent>
          </Sheet>
        </div>

        {/* ── Main layout ──────────────────────────────────────── */}
        <div className="flex flex-col md:flex-row gap-12">

          {/* Desktop static sidebar (hidden on mobile) */}
          <aside className="hidden md:flex w-64 shrink-0 flex-col">
            <SidebarNav />
          </aside>

          {/* Main content */}
          <Reveal
            as="section"
            delay={0.08}
            className="flex-1 bg-surface-container-lowest rounded-xl p-8 shadow-[0_12px_40px_rgba(61,35,20,0.05)] border border-outline-variant/10"
          >
            {/* Shipping Profile */}
            {section === "shipping" && (
              <>
                <header className="border-b border-outline-variant/20 pb-6 mb-6">
                  <h2 className="font-headline-md text-headline-md text-primary mb-2">{t("profile.shippingProfile")}</h2>
                  <p className="font-body-md text-on-surface-variant italic">{t("profile.shippingDesc")}</p>
                </header>
                <form
                  className="grid grid-cols-1 md:grid-cols-2 gap-x-gutter gap-y-8"
                  onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }}
                >
                  <div className="col-span-full md:col-span-1 flex flex-col gap-2">
                    <label className={labelClass} htmlFor="fullName">{t("profile.fields.fullName")}</label>
                    <input id="fullName" type="text" className={inputClass} value={form.fullName}
                      onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))} />
                  </div>
                  <div className="col-span-full md:col-span-1 flex flex-col gap-2">
                    <label className={labelClass} htmlFor="phone">{t("profile.fields.phone")}</label>
                    <input id="phone" type="tel" placeholder={t("profile.placeholders.phone")} className={inputClass}
                      value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
                  </div>
                  <div className="col-span-full flex flex-col gap-2">
                    <label className={labelClass} htmlFor="street">{t("profile.fields.street")}</label>
                    <input id="street" type="text" placeholder={t("profile.placeholders.street")} className={inputClass}
                      value={form.street} onChange={(e) => setForm((p) => ({ ...p, street: e.target.value }))} />
                  </div>
                  <div className="col-span-full md:col-span-1 flex flex-col gap-2">
                    <label className={labelClass} htmlFor="city">{t("profile.fields.city")}</label>
                    <input id="city" type="text" placeholder={t("profile.placeholders.city")} className={inputClass}
                      value={form.city} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} />
                  </div>
                  <div className="col-span-full md:col-span-1 flex flex-col gap-2">
                    <label className={labelClass} htmlFor="governorate">{t("profile.fields.shippingArea")}</label>
                    <select id="governorate" className={`${inputClass} appearance-none`} value={form.governorate}
                      onChange={(e) => setForm((p) => ({ ...p, governorate: e.target.value }))}>
                      <option value="">—</option>
                      {(areas ?? []).map((area) => (
                        <option key={area.id} value={area.name}>{areaLabel(area)}</option>
                      ))}
                    </select>
                    {selectedArea && (
                      <p className="font-label-sm text-label-sm text-on-surface-variant">
                        {formatPrice(selectedArea.price)} · {t("profile.deliveryEstimate", { days: String(selectedArea.days) })}
                      </p>
                    )}
                  </div>
                  <div className="col-span-full mt-8 flex justify-end">
                    <button type="submit" disabled={saveMutation.isPending || !dirty}
                      className="bg-primary text-on-primary px-12 py-4 rounded-lg font-label-md text-label-md uppercase tracking-widest hover:bg-primary/90 transition-all shadow-lg shadow-primary/10 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40">
                      {saveMutation.isPending ? t("common.saving") : t("profile.saveChanges")}
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* My Orders */}
            {section === "orders" && (
              <>
                <header className="border-b border-outline-variant/20 pb-6 mb-6">
                  <h2 className="font-headline-md text-headline-md text-primary mb-2">{t("profile.myOrders")}</h2>
                  <p className="font-body-md text-on-surface-variant italic">{t("profile.ordersDesc")}</p>
                </header>
                <div className="space-y-6">
                  {orders.length === 0 ? (
                    <div className="flex flex-col items-center text-center py-16">
                      <div className="w-40 h-32 md:w-52 md:h-40 text-secondary mb-8">
                        <svg viewBox="0 0 160 120" className="w-full h-full" aria-hidden="true">
                          <ellipse cx="80" cy="108" rx="48" ry="6" fill="currentColor" opacity="0.08" />
                          <rect x="44" y="18" width="72" height="86" rx="6" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.5" />
                          <polyline points="44,104 52,96 60,104 68,96 76,104 84,96 92,104 100,96 108,104 116,96 116,104" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.5" />
                          <line x1="56" y1="38" x2="104" y2="38" stroke="currentColor" strokeWidth="1.5" opacity="0.35" />
                          <line x1="56" y1="52" x2="96" y2="52" stroke="currentColor" strokeWidth="1.5" opacity="0.25" />
                          <line x1="56" y1="64" x2="104" y2="64" stroke="currentColor" strokeWidth="1.5" opacity="0.25" />
                          <line x1="56" y1="76" x2="88" y2="76" stroke="currentColor" strokeWidth="1.5" opacity="0.2" />
                        </svg>
                      </div>
                      <h3 className="font-headline-md text-headline-md text-primary mb-3">{t("profile.myOrders")}</h3>
                      <p className="font-body-md text-on-surface-variant max-w-sm mb-8">{t("profile.noOrders")}</p>
                      <Link to="/shop"
                        className="inline-flex items-center justify-center px-10 py-4 bg-primary text-on-primary rounded-lg font-label-md text-label-md uppercase tracking-widest hover:bg-primary/90 transition-colors gap-2">
                        {t("profile.startShopping")}
                        <Icon name="east" className="text-sm rtl:rotate-180" />
                      </Link>
                    </div>
                  ) : (
                    orders.map((order) => (
                      <div key={order.id} className="border border-outline-variant/30 rounded-xl p-6 hover:shadow-md transition-shadow">
                        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-4 pb-4 border-b border-outline-variant/20">
                          <div>
                            <span className="font-label-sm text-[11px] uppercase tracking-widest text-on-surface-variant mb-1 block">
                              {new Date(order.createdAt).toLocaleDateString()}
                            </span>
                            <h3 className="font-headline-md text-primary">{order.id}</h3>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className={`font-label-md text-label-md px-3 py-1 rounded-full capitalize ${orderStatusBadge(order.status)}`}>
                              {t(`admin.status.${String(order.status).toLowerCase()}`, { defaultValue: order.status })}
                            </span>
                            <span className="font-headline-md text-primary">{formatPrice(order.total)}</span>
                          </div>
                        </div>
                        <Swiper
                          modules={[FreeMode]}
                          freeMode
                          slidesPerView="auto"
                          spaceBetween={16}
                          dir="ltr"
                          className="pb-2"
                        >
                          {order.items?.map((item: any, index: number) => {
                            const src = imgUrl(item.image) || rowImg(item.product) || rowImg(item);
                            return (
                              <SwiperSlide key={item.id ?? `${order.id}-${index}`} className="!w-16">
                                <div className="w-16 h-20 rounded bg-surface-container overflow-hidden">
                                  {src && <img src={src} alt="" className="w-full h-full object-cover" />}
                                </div>
                              </SwiperSlide>
                            );
                          })}
                        </Swiper>

                      </div>
                    ))
                  )}
                </div>
              </>
            )}

            {/* Personal Info */}
            {section === "personal" && (
              <>
                <header className="border-b border-outline-variant/20 pb-6 mb-6">
                  <h2 className="font-headline-md text-headline-md text-primary mb-2">{t("profile.personalInfo")}</h2>
                  <p className="font-body-md text-on-surface-variant italic">{t("profile.personalDesc")}</p>
                </header>
                <dl className="space-y-4 font-body-md">
                  <div className="flex justify-between border-b border-outline-variant/20 pb-3">
                    <dt className="text-on-surface-variant">{t("profile.name")}</dt>
                    <dd className="text-primary">{displayName}</dd>
                  </div>
                  <div className="flex justify-between border-b border-outline-variant/20 pb-3">
                    <dt className="text-on-surface-variant">{t("profile.email")}</dt>
                    <dd className="text-primary">{user?.email ?? t("profile.notSignedIn")}</dd>
                  </div>
                </dl>
              </>
            )}
          </Reveal>
        </div>
      </main>
    </StoreLayout>
  );
}

export default ProfilePage;
